// packages import
import { Box, TableBody, TableCell, TableHead, TableRow, useMediaQuery } from '@mui/material'
import { FC, useContext } from 'react'
import { useSearchParams } from 'react-router-dom'
// component import
import { ContentHead } from '../../common/ContentHead'
import { CustomPagination } from '../../common/CustomPagination'
import { EmptyData } from '../../common/EmptyData'
import { UploadCsv } from '../../common/UploadCsv'
import { CasesSkeleton } from '../../skeletons/CasesSkeleton'
import { FilterWrapper } from '../FilterWrapper'
import { CompleteCase } from './CompleteCase'
import { DisplayCaseItem } from './DisplayCaseItem'
// others
import { MY_CASES_TABLE_HEADERS, QUERY_PARAMS } from '../../../constants'
import { AppContext, AuthContext, CaseContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { CaseProps } from '../../../interfaceTypes'
import { CardBox, CustomTabButton, FilterOuterWrapper } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { CaseActionType } from '../caseReducer'
import { CaseStickyTable } from '../../common/CaseStickyTable'

export const MyCases: FC<CaseProps> = ({ loading, refetch, isConsultPath = false }): JSX.Element => {
  const { dispatch: reducerDispatch } = useContext(AppContext)
  const { isInvestigator, isAttorney, isAdmin, isParalegal } = useContext(AuthContext)
  const { dispatch, caseFilter, allCases, isWorkCompleted } = useContext(CaseContext)
  const isEmpty = allCases?.length === 0 && !loading
  const casesLength = allCases?.length
  const [searchParams, setSearchParams] = useSearchParams()
  const mediumViewport = useMediaQuery('(max-width:1138px)')
  const isParalegalOrAttorney = isParalegal || isAttorney

  return (
    <CardBox marginTop={3}>
      <ContentHead
        title="My Matters"
        buttonText={isInvestigator ? '' : `Add ${isConsultPath ? 'Consult' : 'a New Matter'}`}
        link="create"
        csvButton={isAdmin ? 'Upload Csv' : ''}
        isCase
      />

      <Box padding="20px 0px 0px 15px">
      <CustomTabButton
          isLeftItem
          onClick={() => {
            dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
            dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
            dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: '' })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: false })
            searchParams.set('caseFilter', 'active')
            searchParams.set('currentPage', '1')
            searchParams.set('searchValue', '')
            searchParams.set('lawType', '')
            searchParams.set(QUERY_PARAMS.IS_WORK_COMPLETED, 'no')
            setSearchParams(searchParams)
          }}
          className={caseFilter === 'active' && !isWorkCompleted ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
        >
          Active
        </CustomTabButton>

        <CustomTabButton
          isCenterItem
          onClick={() => {
            dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
            dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
            dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: '' })
            dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: true })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            searchParams.set('caseFilter', 'active')
            searchParams.set('currentPage', '1')
            searchParams.set('searchValue', '')
            searchParams.set('lawType', '')
            searchParams.set(QUERY_PARAMS.IS_WORK_COMPLETED, 'yes')
            setSearchParams(searchParams)
          }}
          className={isWorkCompleted ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
        >
          Work Completed
        </CustomTabButton>

        <CustomTabButton
          onClick={() => {
            dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'close' })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
            dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: '' })
            dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: undefined })
            searchParams.set('caseFilter', 'close')
            searchParams.set('currentPage', '1')
            searchParams.set('searchValue', '')
            searchParams.set('lawType', '')
            searchParams.delete(QUERY_PARAMS.IS_WORK_COMPLETED)
            setSearchParams(searchParams)
          }}
          className={caseFilter === 'close' ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
        >
          Closed
        </CustomTabButton>
      </Box>

      <FilterOuterWrapper justifyContent={mediumViewport ? 'flex-end' : 'space-between'}>
        <FilterWrapper casesLength={casesLength!} />
      </FilterOuterWrapper>

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData
            title={
              caseFilter === 'close'
                ? `No closed ${isConsultPath ? 'consults' : 'matters'}`
                : `You have no ${isConsultPath ? 'consults' : 'matters'} assigned`
            }
          />
        </Box>
      ) : (
        <CaseStickyTable
          pagination={<CustomPagination loading={loading} setParams />}
          loading={loading}
          height={isParalegalOrAttorney ? (mediumViewport ? '435px' : '498px') : '425px'}
        >
          <TableHead>
            <TableRow>
              {MY_CASES_TABLE_HEADERS.map((item, index) => {
                const lastIndex = MY_CASES_TABLE_HEADERS.length - 1

                return (
                  <TableCell
                    key={`${item}-${index}`}
                    component="th"
                    align={index === lastIndex ? 'center' : 'left'}
                    sx={{ minWidth: index === lastIndex ? '50px' : '150px ' }}
                  >
                    {item}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>

          {loading ? (
            <CasesSkeleton noOfRows={6} />
          ) : (
            <TableBody>
              <DisplayCaseItem IsMyCases />
            </TableBody>
          )}
        </CaseStickyTable>
      )}

      <CompleteCase refetch={refetch} />
      <UploadCsv isCase refetchCases={refetch} />
    </CardBox>
  )
}
